import NUIAlert from '@/components/Alert/Alert'
import { IConfigSeller } from '@/models/configSeller'
import { IAppStore } from '@/models/store.model'
import { webApiService } from '@/services'
import { blobToBase64 } from '@/utils/blobToBase64.util'
import { currencyFormat } from '@/utils/currencyFormat'
import { generateBarcodeBase64 } from '@/utils/makeBarCode.util'
import { validateRUT } from '@/utils/validateRUT.util'
import { Autocomplete, AutocompleteItem, Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner } from '@nextui-org/react'
import { Document, Page, pdf } from '@react-pdf/renderer'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import QRCode from 'qrcode'
import { FC, useEffect, useState } from 'react'
import { MdDone } from 'react-icons/md'
import Html from 'react-pdf-html'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useTradeinContext } from '../hooks/useTradeinContext'
import WomPurchaseFields, { IWomPurchase, emptyWomPurchase, formatBoost, isWomPurchaseComplete, womDeviceName, womDevicePurchased } from './WomPurchaseFields'

// Botón "Recibir equipo" con su modal de datos del vendedor. Es independiente del header para
// poder usarlo también desde el listado, con las mismas condiciones de aparición.
// autoOpen: se monta ya abierto y sin su propio botón. Lo usa el listado, que dibuja el botón con
// los datos que ya tiene y monta este componente (y su provider) solo cuando se hace click, para no
// pedir el detalle de todas las filas.
const ReceiveDevice: FC<{ autoOpen?: boolean, onClosed?: () => void, onDone?: () => void }> = ({ autoOpen, onClosed, onDone }) => {
  const [data, setData] = useState<{ loading: boolean, open: boolean, rut: string }>({ loading: false, open: autoOpen === true, rut: '' })
  const [womPurchase, setWomPurchase] = useState<IWomPurchase>(emptyWomPurchase)
  // WOM: el guardado del plan/modelo es irreversible, así que primero se pide confirmación.
  const [confirmWom, setConfirmWom] = useState<boolean>(false)
  const [purchasedBefore, setPurchasedBefore] = useState<any[]>([])
  const [loadingDevices, setLoadingDevices] = useState<boolean>(false)
  const [modalContainer, setModalContainer] = useState<HTMLElement | null>(null)
  const { ctx, saveCtx } = useTradeinContext()
  const { slug } = useParams()
  const userState = useSelector((store: IAppStore) => store.user)
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const isCoppel = configSeller.config.s3.FOLDER_APP === 'ti_coppel'
  const isWom = configSeller.config.s3.FOLDER_APP === 'ti_wom'
  const isXiaomi = configSeller.config.s3.FOLDER_APP === 'ti_xiaomi'
  const isOppoMexico = configSeller.config.s3.FOLDER_APP === 'ti_oppo'
  const isElektraMexico = configSeller.config.s3.FOLDER_APP === 'ti_elektra_mx'
  const isFalabellaPeru = configSeller.config.s3.FOLDER_APP === 'ti_falabella_pe'
  const isFalabellaChile = configSeller.config.s3.FOLDER_APP === 'ti_falabella'
  const isPhantom = configSeller.config.s3.FOLDER_APP === 'ti_phantom'
  const isRipleyPeru = configSeller.config.s3.FOLDER_APP === 'ti_ripley_pe'
  const isRipleyChile = configSeller.config.s3.FOLDER_APP === 'ti_ripley_cl'
  const isTecnobuyChile = configSeller.config.s3.FOLDER_APP === 'ti_tecnobuy_cl'
  const isMacstoreMexico = configSeller.config.s3.FOLDER_APP === 'ti_macstore'
  const isSamsungMexico = configSeller.config.s3.FOLDER_APP === 'ti_samsung_mx'
  const isParisChile = configSeller.config.s3.FOLDER_APP === 'ti_paris'
  const isHuaweiMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_mx'
  const isTelcelMexico = configSeller.config.s3.FOLDER_APP === 'ti_telcel_mx'
  const isTelcelCorpMexico = configSeller.config.s3.FOLDER_APP === 'ti_telcel_corp_mx'
  const isHuaweiPeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_pe'
  const isHuaweiChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_cl'
  const isHuaweiOnlineChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_cl'
  const isHuaweiOnlineMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_mx'
  const isHuaweiOnlinePeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_pe'
  const isLiverpoolMexico = configSeller.config.s3.FOLDER_APP === 'ti_liverpool_mx'
  const hasPhonecheck = ctx.tradein.id_sbu === 1 || (ctx.tradein.id_sbu === 3 && ctx.tradein.brand === 'Apple')

  const handleRut = () => {
    setData({ ...data, open: true })
  }

  const handleClose = () => {
    setData({ ...data, open: false })
    if (onClosed) onClosed()
  }

  const handleRecibir = () => {
    setConfirmWom(false)
    setData({ ...data, loading: true })
    webApiService.update({
      id_tradein: String(ctx.tradein.id),
      id_user: userState.id,
      user_email: userState.email,
      stage: 6,
      id_role: userState.id_role,
      email: userState.email,
      client_email: ctx.tradein.email,
      id_seller: ctx.tradein.id_seller,
      rut: data.rut,
      client_name: ctx.tradein.name,
      client_lastname: ctx.tradein.lastname,
      url_contract: ctx.tradein.url_contract,
      recipient_name: isWom ? userState.name : ctx.tradein.recipient_name,
      recipient_lastname: ctx.tradein.recipient_lastname,
      recipient_rut: isWom ? userState.rut : ctx.tradein.recipient_rut || '',
      recipient_id: ctx.tradein.recipient_id || '',
      // WOM: device_purchased va como objeto (modelo completo + plan + extra bono), no como texto.
      device_purchased: isWom ? womDevicePurchased(womPurchase) : ctx.tradein.device_purchased,
      ...(isWom
        ? {
            wom_plan: womPurchase.plan,
            wom_boost: womPurchase.boost
          }
        : {})
    })
      .then((data) => {
        if (data.data.folio && isFalabellaPeru) {
          updateContract(data.data)
        } else if (isPhantom) {
          console.log('Updating Phantom contract, data:', data.data)
          updateContractPhantom(data.data)
        } else if ((isRipleyChile && data.data.status === 500) || (isRipleyChile && data.data.cardNumber?.success === false)) {
          enqueueSnackbar(data.data.errorRipley, { variant: 'error' })
          setData({ ...data, loading: false, rut: '' })
        } else {
          enqueueSnackbar('Estado actualizado a Entregado', { variant: 'success' })
          setData({ ...data, loading: false, rut: '' })
          console.log('data', data)
          if (onDone) onDone()
          else saveCtx({ ...ctx, reload: true })
        }
      })
      .catch((error: any) => {
        if (error.status === 500 && error.response.data !== undefined) {
          if (isRipleyChile) {
            enqueueSnackbar('Error generando la Giftcard', { variant: 'error' })
          } else {
            enqueueSnackbar(error.response.data.message, { variant: 'error' })
          }
          enqueueSnackbar('Recargando pagina', { variant: 'warning' })
          saveCtx({ ...ctx, reload: true })
        } else {
          if (isRipleyChile) {
            enqueueSnackbar('Error generando la Giftcard', { variant: 'error' })
          } else {
            enqueueSnackbar(error.message, { variant: 'error' })
          }
        }

        setData({ ...data, loading: false, rut: '' })
        console.log('error ==>', error)
      })
  }

  const updateContract = async (data: any) => {
    const fecha = moment().locale('es').format('DD/MM/YYYY')
    const fullname = String(ctx.tradein.name) + ' ' + String(ctx.tradein.lastname)
    const identificacion: string = ctx.tradein.rut
    const marca: string = ctx.tradein.brand
    const modelo: string = ctx.tradein.model
    const imei: string = ctx.tradein.serie
    const discount = currencyFormat(Number(ctx.tradein.discount_amount), import.meta.env.VITE_COUNTRY)
    const address: string = ctx.tradein.address + ', ' + ctx.tradein.city
    const sbu: string = ctx.tradein.sbu
    const civilState: string = String(ctx.tradein.civil_state)
    const barCodeText1 = data.folio
    const barCodeText2 = String(ctx.tradein.discount_amount)
    console.log('barCodeText1 ==> ', barCodeText1)

    const urlFirma = `https://${import.meta.env.VITE_BUCKET}.s3.us-west-2.amazonaws.com/${configSeller.config.s3.FOLDER_APP}/contratos/${ctx.tradein.id}/${data.contractName.replace('contrato.pdf', 'Signature.png')}`
    console.log('urlFirma ==> ', urlFirma)

    const htmlWidthReplace = configSeller.config.html.replaceAll('{{fecha}}', fecha)
      .replaceAll('{{fullname}}', fullname)
      .replaceAll('{{identificacion}}', identificacion)
      .replaceAll('{{marca}}', marca)
      .replaceAll('{{modelo}}', modelo)
      .replaceAll('{{imei}}', imei)
      .replaceAll('{{discount}}', discount)
      .replaceAll('{{domicilio}}', address)
      .replaceAll('{{sbu}}', sbu)
      .replaceAll('{{estadoCivil}}', civilState)
      .replace('{{barCode1}}', '<img width="250px" src="' + String(generateBarcodeBase64(barCodeText1)) + '">')
      .replace('{{barCode2}}', '<img width="250px" src="' + String(generateBarcodeBase64(barCodeText2)) + '">')
      .replace('{{firma}}', '<img style="height:80px" src="' + urlFirma + '">')

    console.log('htmlWidthReplace ==> ', htmlWidthReplace)

    // convertir el html a pdf
    const pdfBlobMacstore = await pdf(<Document> <Page size='A4' style={{ fontFamily: 'Helvetica', fontWeight: 'bold' }}> <Html style={{ margin: '40px' }} resetStyles={false}>{htmlWidthReplace.replaceAll(/Times New Roman/gi, 'Helvetica')}</Html> </Page></Document>).toBlob()
    console.log('🚀 ~ pdfBlobMacstore:', pdfBlobMacstore)
    const pdfWidthFolio = await blobToBase64(pdfBlobMacstore)
    console.log('🚀 ~ pdfWidthFolio:', pdfWidthFolio)

    await webApiService.updateContract({ pdf: pdfWidthFolio, seller: slug, id_tradein: ctx.tradein.id, contractName: data.contractName, name: fullname, email: ctx.tradein.email }).then(res => {
      console.log('🚀 ~ awaitwebApiService.signContract ~ res:', res)
      enqueueSnackbar('Estado actualizado a Entregado', { variant: 'success' })
      setData({ ...data, loading: false, rut: '' })
      console.log('data', data)
      saveCtx({ ...ctx, reload: true })
    }).catch((error : any) => {
      console.log('🚀 ~ awaitwebApiService.signContract ~ error:', error)
      enqueueSnackbar('Error al firmar', { variant: 'error' })
      setData({ ...data, loading: false })
    })

    // Download PDF pdfBlobMacstore
  }

  const updateContractPhantom = async (data: any) => {
    const fecha = moment().locale('es').format('DD/MM/YYYY')
    const day = moment().locale('es').format('DD')
    const month = moment().locale('es').format('MM')
    const year = moment().locale('es').format('YYYY')
    const direccion = ctx.tradein.address + ', ' + ctx.tradein.city
    const fullname = String(ctx.tradein.name) + ' ' + String(ctx.tradein.lastname)
    const rut: string = ctx.tradein.rut
    const email: string = ctx.tradein.email
    const discount = currencyFormat(Number(ctx.tradein.discount_amount), import.meta.env.VITE_COUNTRY)
    const contractName = ctx.tradein.url_contract.split('/').pop() || ''
    const qrCodeData = {
      marca: ctx.tradein.brand,
      modelo: ctx.tradein.model,
      capacidad: ctx.tradein.capacity,
      descuento: ctx.tradein.discount_amount
    }

    const qrCodeUrl = await QRCode.toDataURL(JSON.stringify(qrCodeData))
    console.log('qrCodeUrl ==> ', qrCodeUrl)
    console.log('contractName ==> ', data.contractName)
    const urlFirma = `https://${import.meta.env.VITE_BUCKET}.s3.us-west-2.amazonaws.com/${configSeller.config.s3.FOLDER_APP}/contratos/${ctx.tradein.id}/${contractName.replace('contrato.pdf', 'Signature.png')}`
    console.log('urlFirma ==> ', urlFirma)

    const htmlWidthReplace = configSeller.config.html.replaceAll('{{fecha}}', fecha)
      .replaceAll('{{fullname}}', fullname)
      .replaceAll('{{day}}', day)
      .replaceAll('{{month}}', month)
      .replaceAll('{{year}}', year)
      .replaceAll('{{rut}}', rut)
      .replaceAll('{{domicilio}}', direccion)
      .replaceAll('{{email}}', email)
      .replaceAll('{{discount}}', discount)
      .replace('{{qrCode}}', '<img style="height:150px;width:150px" src="' + qrCodeUrl + '">')
      .replace('{{firma}}', '<img style="height:80px" src="' + urlFirma + '">')

    console.log('htmlWidthReplace ==> ', htmlWidthReplace)

    // convertir el html a pdf
    const pdfBlobPhantom = await pdf(
      <Document>
        <Page size='A4' style={{ fontFamily: 'Helvetica', fontWeight: 'bold', fontSize: 12 }}>
          <Html style={{ margin: '40px', fontSize: '12px' }} resetStyles={false}>
            {htmlWidthReplace.replaceAll(/Times New Roman/gi, 'Helvetica')}
          </Html>
        </Page>
      </Document>
    ).toBlob()
    console.log('🚀 ~ pdfBlobPhantom:', pdfBlobPhantom)
    const pdfWidthFolio = await blobToBase64(pdfBlobPhantom)
    console.log('🚀 ~ pdfWidthFolio:', pdfWidthFolio)

    await webApiService.updateContract({ pdf: pdfWidthFolio, seller: slug, id_tradein: ctx.tradein.id, contractName, name: fullname, email: ctx.tradein.email }).then(res => {
      console.log('🚀 ~ awaitwebApiService.signContract ~ res:', res)
      enqueueSnackbar('Estado actualizado a Entregado', { variant: 'success' })
      setData({ ...data, loading: false, rut: '' })
      console.log('data', data)
      saveCtx({ ...ctx, reload: true })
    }).catch((error : any) => {
      console.log('🚀 ~ awaitwebApiService.signContract ~ error:', error)
      enqueueSnackbar('Error al firmar', { variant: 'error' })
      setData({ ...data, loading: false })
    })
  }

  useEffect(() => {
    // El listado de dispositivos comprados solo se usa en el autocomplete de Samsung y Telcel.
    if (!isSamsungMexico && !isTelcelMexico && !isTelcelCorpMexico) return

    const checkPurchasedBefore = async () => {
      try {
        setLoadingDevices(true)
        const res = await webApiService.getAllDevicesPurchased()
        console.log('res checkPurchasedBefore => ', res)
        const temp = res.data.map((item: any) => ({ id: item.id, name: item.device }))
        setPurchasedBefore(temp)
        setLoadingDevices(false)
      } catch (error) {
        setLoadingDevices(false)
        console.log('error checkPurchasedBefore => ', error)
      }
    }

    checkPurchasedBefore()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Mismas condiciones con las que se mostraba el botón en el header del detalle.
  const canReceive = (
    (
      !isFalabellaPeru &&
      (((ctx.tradein.id_status === 3 || (ctx.displayContract === 0 && ctx.tradein.id_status === 4)) &&
      ctx.tradein.hasFiles === 1) || ((isSamsungMexico || isHuaweiMexico || isRipleyChile || isHuaweiPeru || isHuaweiChile || isTelcelMexico || isTelcelCorpMexico || isHuaweiOnlineChile || isHuaweiOnlineMexico || isHuaweiOnlinePeru || isXiaomi || isOppoMexico || isElektraMexico || isCoppel || (isFalabellaChile && hasPhonecheck)) && ctx.tradein.id_status === 3)) &&
      ![Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)].includes(userState.id_role)
    ) ||
    (
      isFalabellaPeru &&
      (ctx.tradein.id_status === 3 || (ctx.displayContract === 0 && ctx.tradein.id_status === 4)) &&
      ctx.tradein.hasFiles === 1 &&
      ![Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR), Number(import.meta.env.VITE_PERFIL_EJECUTIVO), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CUSTODIA)].includes(userState.id_role)
    )
  )

  // Con autoOpen la condición ya se evaluó afuera (listado), acá solo se muestra el modal.
  if (!autoOpen && !canReceive) return null

  // El detalle se pide al montar el provider, así que puede no estar listo cuando el modal ya está abierto.
  const loadingDetail = ctx.tradein.id === 0

  return (
    <>
      {!autoOpen && (
        <Button
          className='step-recibir'
          color='primary'
          variant='shadow'
          size='sm'
          style={{ marginLeft: '5px' }}
          endContent={<MdDone style={{ fontSize: '15px' }} />}
          onClick={String(configSeller.config.s3.FOLDER_APP) === 'ti_falabella' || String(configSeller.config.s3.FOLDER_APP) === 'ti_macstore' || String(configSeller.config.s3.FOLDER_APP) === 'ti_wom' || String(configSeller.config.s3.FOLDER_APP) === 'ti_oppo' || String(configSeller.config.s3.FOLDER_APP) === 'ti_elektra_mx' || String(configSeller.config.s3.FOLDER_APP) === 'ti_samsung_pe_rom' || String(configSeller.config.s3.FOLDER_APP) === 'ti_conecta_peru' || String(configSeller.config.s3.FOLDER_APP) === 'ti_coppel' || String(configSeller.config.s3.FOLDER_APP) === 'ti_samsung_mx' || String(configSeller.config.s3.FOLDER_APP) === 'ti_ripley_pe' || String(configSeller.config.s3.FOLDER_APP) === 'ti_paris' || String(configSeller.config.s3.FOLDER_APP) === 'ti_ripley_cl' || String(configSeller.config.s3.FOLDER_APP) === 'ti_telcel_mx' || String(configSeller.config.s3.FOLDER_APP) === 'ti_telcel_corp_mx' || String(configSeller.config.s3.FOLDER_APP) === 'ti_mercado_libre_cl' || String(configSeller.config.s3.FOLDER_APP) === 'ti_tecnobuy_cl' || String(configSeller.config.s3.FOLDER_APP) === 'ti_liverpool_mx' ? handleRut : handleRecibir}
          isLoading={data.loading}
        >
          {(String(configSeller.config.s3.FOLDER_APP) === 'ti_falabella' || String(configSeller.config.s3.FOLDER_APP) === 'ti_conecta_peru' || String(configSeller.config.s3.FOLDER_APP) === 'ti_ripley_cl') ? 'Recibir y emitir giftcard' : 'Recibir equipo'}
        </Button>
      )}
      <Modal backdrop='blur' size='lg' scrollBehavior='inside' isOpen={data.open} onClose={handleClose}>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>{isWom ? 'Datos de la compra' : 'Datos Vendedor'}</ModalHeader>
          <ModalBody>
            {loadingDetail && <div className='flex justify-center py-6'><Spinner label='Cargando datos...' /></div>}
            {!loadingDetail && <NUIAlert type='warning'>¿El cliente te ha entregado el dispositivo restablecido, desvinculado y sin contraseñas de acceso?</NUIAlert>}
            {/* WOM solo pide el modelo comprado y el plan, sin los datos del vendedor. */}
            {!loadingDetail && (!isSamsungMexico && !isTelcelMexico && !isTelcelCorpMexico && !isWom) && (
              <>
                <div>
                  <Input
                    type='text'
                    variant='bordered'
                    label={isLiverpoolMexico ? 'Nombre del vendedor que recibe la consola' : 'Nombre del vendedor'}
                    value={ctx.tradein.recipient_name}
                    fullWidth
                    autoComplete='off'
                    onChange={(e) => saveCtx({ ...ctx, tradein: { ...ctx.tradein, recipient_name: e.target.value } })}
                  />
                </div>
                <div>
                  <Input
                    type='text'
                    variant='bordered'
                    label={isLiverpoolMexico ? 'Apellido del vendedor que recibe la consola' : 'Apellido del vendedor'}
                    value={ctx.tradein.recipient_lastname}
                    fullWidth
                    autoComplete='off'
                    onChange={(e) => saveCtx({ ...ctx, tradein: { ...ctx.tradein, recipient_lastname: e.target.value } })}
                  />
                </div>
                {
                  !isFalabellaChile && !isLiverpoolMexico && (
                    <div className='col-span-2'>
                      <Input
                        type='text'
                        variant='bordered'
                        label={isRipleyPeru ? 'Código del vendedor' : (isMacstoreMexico) ? 'Número de vendedor' : isTecnobuyChile ? `${import.meta.env.VITE_LABEL_RUT} del vendedor (sin puntos y sin guion)` : `${import.meta.env.VITE_LABEL_RUT} del ${isParisChile || isRipleyChile ? 'vendedor (sin puntos y con guion)' : 'receptor'}`}
                        value={ctx.tradein.recipient_rut}
                        fullWidth
                        autoComplete='off'
                        onChange={(e) => saveCtx({ ...ctx, tradein: { ...ctx.tradein, recipient_rut: isTecnobuyChile ? e.target.value.replace(/-/g, '') : e.target.value } })}
                        isInvalid={(isRipleyChile || isTecnobuyChile) && (ctx.tradein.recipient_rut ?? '') !== '' && !validateRUT(ctx.tradein.recipient_rut ?? '')}
                        errorMessage={(isRipleyChile || isTecnobuyChile) && (ctx.tradein.recipient_rut ?? '') !== '' && !validateRUT(ctx.tradein.recipient_rut ?? '') ? 'Ingrese un RUT válido' : undefined}
                      />
                    </div>
                  )
                }
                {
                  isFalabellaChile && (
                    <div className='col-span-2'>
                      <Input
                        type='text'
                        variant='bordered'
                        label='Código del vendedor'
                        value={ctx.tradein.recipient_id}
                        fullWidth
                        autoComplete='off'
                        onChange={(e) => saveCtx({ ...ctx, tradein: { ...ctx.tradein, recipient_id: e.target.value } })}
                      />
                    </div>
                  )
                }
              </>
            )}

            <div className='col-span-2' ref={setModalContainer}>
              {
              !loadingDetail && !loadingDevices && (isSamsungMexico || isTelcelMexico || isTelcelCorpMexico)
                ? (
                  <Autocomplete
                    allowsCustomValue
                    defaultItems={purchasedBefore}
                    label='Asigne nombre de compra'
                    placeholder='Buscar dispositivo o marca'
                    popoverProps={{
                      portalContainer: modalContainer ?? undefined,
                      placement: 'bottom',
                      classNames: { content: 'max-h-[320px]' }
                    }}
                    listboxProps={{ className: 'max-h-[280px]' }}
                    onInputChange={(value) => {
                      saveCtx({ ...ctx, tradein: { ...ctx.tradein, device_purchased: value } })
                    }}
                    onSelectionChange={(key) => {
                      if (key === null) return
                      const selected = purchasedBefore.find((item) => String(item.id) === String(key))
                      if (selected) {
                        saveCtx({ ...ctx, tradein: { ...ctx.tradein, device_purchased: selected.name } })
                      }
                    }}
                  >
                    {(item) => (
                      <AutocompleteItem key={item.id}>{item.name}</AutocompleteItem>
                    )}
                  </Autocomplete>
                  )
                : null
              }
              {!loadingDetail && isWom && (
                <div className='flex flex-col gap-3'>
                  <WomPurchaseFields
                    value={womPurchase}
                    onChange={setWomPurchase}
                    portalContainer={modalContainer}
                  />
                </div>
              )}
            </div>
            <p hidden={String(configSeller.config.s3.FOLDER_APP) !== 'ti_falabella'}>Si aprietas "Ok" se emitirá la Gift Card</p>
          </ModalBody>
          <ModalFooter>
            <Button
              color='danger'
              onClick={handleClose}
            >
              Cerrar
            </Button>
            <Button
              color='primary'
              type='submit'
              onClick={isWom ? () => setConfirmWom(true) : handleRecibir}
              isDisabled={
                loadingDetail ||
                (isRipleyChile && !validateRUT(ctx.tradein.recipient_rut ?? '')) ||
                (isWom && !isWomPurchaseComplete(womPurchase)) ||
                (!isWom &&
                  ((!isSamsungMexico && !isTelcelMexico && !isTelcelCorpMexico)
                    ? (
                        ctx.tradein.recipient_name === '' ||
                        ctx.tradein.recipient_name === undefined ||
                        ctx.tradein.recipient_lastname === '' ||
                        ctx.tradein.recipient_lastname === undefined ||
                        // El RUT/número de vendedor solo se exige donde el campo se muestra: Liverpool no lo
                        // pide y Falabella Chile lo reemplaza por el código de vendedor, así que exigirlo
                        // siempre dejaba el botón deshabilitado para siempre en esos dos sellers.
                        (!isFalabellaChile && !isLiverpoolMexico && (ctx.tradein.recipient_rut === '' || ctx.tradein.recipient_rut === undefined)) ||
                        (isFalabellaChile && (ctx.tradein.recipient_id === '' || ctx.tradein.recipient_id === undefined))
                      )
                    : (
                        ctx.tradein.device_purchased === '' ||
                        ctx.tradein.device_purchased === undefined ||
                        ctx.tradein.device_purchased === null
                      )))
              }
              isLoading={data.loading}
            >
              {data.loading ? 'Enviando...' : 'Ok'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* WOM: confirmación previa al guardado. Si cancelan vuelven al modal de entrega con sus
          selecciones intactas; recién al aceptar se envía. */}
      <Modal backdrop='blur' size='md' isOpen={confirmWom} onClose={() => setConfirmWom(false)}>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>Confirmar plan y dispositivo</ModalHeader>
          <ModalBody>
            <NUIAlert type='warning'>¿Está seguro que desea guardar este plan y dispositivo? Una vez guardado no podrá modificarse.</NUIAlert>
            <div className='rounded-medium border border-default-200 p-3'>
              <p className='text-small'>Plan: <span className='font-bold'>{womPurchase.plan}</span></p>
              <p className='text-small'>Dispositivo: <span className='font-bold'>{womDeviceName(womPurchase.device)}</span></p>
              <p className='text-small'>Extra Bono: <span className='font-bold'>{formatBoost(womPurchase.boost)}</span></p>
              <p className='text-small'>Monto retoma: <span className='font-bold'>{currencyFormat(Number(ctx.tradein.discount_amount), import.meta.env.VITE_COUNTRY)}</span></p>
              <p className='text-small'>Monto total final: <span className='font-bold'>{currencyFormat(Number(ctx.tradein.discount_amount) + Number(womPurchase.boost ?? 0), import.meta.env.VITE_COUNTRY)}</span></p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color='danger' variant='light' onClick={() => setConfirmWom(false)}>
              Cancelar
            </Button>
            <Button color='primary' onClick={handleRecibir} isLoading={data.loading}>
              Aceptar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ReceiveDevice
